import React, { memo, useCallback, useEffect, useState } from "react";
import { Button, OverlayTrigger, Spinner } from "react-bootstrap";

import { ErrorNotification } from "../../../../components/CustomModal/ErrorNotifacation";
import { CustomTextArea } from "../../../../components/CustomTextArea/CustomTextArea";
import { QuestionIcon } from "../../../../components/icons/QuestionIcon";
import { RelatedIcon } from "../../../../components/icons/RelatedIcon";
import { TrashIcon } from "../../../../components/icons/TrashIcon";
import { renderTooltip } from "../../../../components/StyledTooltip/StyledTooltip";
import type { ISimilarPrompt } from "../../../../hooks/reactQueryHooks/useCacheItems";
import { useAddSimilarPrompts } from "../../../../hooks/reactQueryHooks/useCacheItems";
import type { IPrompt } from "./PromptAndAnswerTab";
import { SavedSimilarPrompt } from "./SavedSimilarPrompt";

export interface IProps {
  prompts: IPrompt[];
  cacheItemId: string;
  orgId: string;
  applicationId: string;
}

const SimilarPromptsComponent: React.FC<IProps> = memo(
  ({ prompts, cacheItemId, orgId, applicationId }) => {
    const [internalPrompts, setInternalPrompts] = useState(prompts);

    const [defaultText, setDefaultText] = useState({
      value: "",
    });

    const [autofocus, setAutofocus] = useState(false);
    const [inputValue, setInputValue] = useState<string>("");
    const [error, setError] = useState<string>("");

    const addMutation = useAddSimilarPrompts();

    useEffect(() => {
      setInternalPrompts(prompts);
    }, [prompts]);

    const handlePromptChange = useCallback((text: string) => {
      setInputValue(text);
      setError("");
    }, []);

    const handleEnter = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (!inputValue.trim() || addMutation.isLoading) return;
        addMutation.reset();
        setError("");
        addMutation.mutate({
          prompts: [inputValue],
          organizationId: orgId,
          cacheItemId,
          applicationId: applicationId,
        });
      },
      [inputValue, addMutation, orgId, cacheItemId, applicationId],
    );

    const deleteLocalPrompt = () => {
      setDefaultText({ value: "" });
      setInputValue("");
      setError("");
    };

    useEffect(() => {
      if (addMutation.isSuccess) {
        setInternalPrompts(prev => {
          return [...prev, ...addMutation.data];
        });
        deleteLocalPrompt();
        setAutofocus(true);
      }
    }, [addMutation.isSuccess, addMutation.data]);

    useEffect(() => {
      if (addMutation.isError) {
        if (addMutation.error.response?.data.type === "Conflict") {
          setError("This similar prompt already exists. Try a different one.");
        } else {
          setError("Sorry, something went wrong. Please try later.");
        }
      }
    }, [addMutation.isError, addMutation.error?.response?.data.type]);

    const handleDeleteSuccess = useCallback(
      (prompts: ISimilarPrompt[]) => {
        setInternalPrompts(prompts);
      },
      [setInternalPrompts],
    );

    return (
      <>
        <p className="similar-prompts prompt-answer__title">
          <RelatedIcon />
          &nbsp; Similar prompts
          <OverlayTrigger
            placement="top"
            overlay={renderTooltip(
              "Adding similar prompts will increase the chances for this cache item to be returned.",
            )}
          >
            <span className="title-question-icon">
              <QuestionIcon />
            </span>
          </OverlayTrigger>
        </p>
        {internalPrompts.map(prompt => (
          <SavedSimilarPrompt
            key={prompt.id}
            prompt={prompt}
            onDeleteSuccess={handleDeleteSuccess}
            orgId={orgId}
            cacheItemId={cacheItemId}
            applicationId={applicationId}
          />
        ))}
        <div className="mb-8">
          <div>
            <div className="d-flex position-relative ">
              <CustomTextArea
                defaultText={defaultText}
                onChange={handlePromptChange}
                placeholder="Add a similar prompt here..."
                onEnter={handleEnter}
                value={inputValue}
                disabled={addMutation.isLoading}
                autofocus={autofocus}
              />
              {inputValue && (
                <>
                  {addMutation.isLoading ? (
                    <div className="d-flex justify-content-center  mt-2 ">
                      <Spinner
                        className="similar-prompts__spinner"
                        size="sm"
                        variant="primary"
                        data-testid="spinner"
                      />
                    </div>
                  ) : (
                    <Button
                      variant="light"
                      className="delete-button d-flex cursor-pointer align-items-center justify-content-center  ms-4"
                      onClick={deleteLocalPrompt}
                    >
                      <TrashIcon />
                    </Button>
                  )}
                </>
              )}
            </div>
            {inputValue.trim() && !error && !addMutation.isLoading && (
              <p className="similar-prompts__hint">Hit Enter/Return to save</p>
            )}
            {error && <ErrorNotification message={error} />}
          </div>
        </div>
      </>
    );
  },
);

export { SimilarPromptsComponent };
